import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SidebarNavMenuItem } from '../../../../decorators/sidebar-nav-menu-item';
import {
  VmConnectionTemplateDetail,
  VmConnectionTemplateSummary,
  VmConnectionProtocol,
} from '../../../../interfaces/vm-connection-templates/vm-connection-template.interface';
import { VmConnectionTemplateService } from '../../../../services/entity-services/vm-connection-template.service';
import { Log } from '../../../../services/logger.service';
import { UserPrefsService } from '../../../../services/user-prefs.service';
import { AuthService } from '../../../../services/auth.service';
import { isParticipant } from '../../../../utils/auth.utils';
import { ConfirmDialogService } from '../../../../services/confirm-dialog.service';
import { NotificationService } from '../../../../services/notification.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../shared/pagination-options';

/** Lookup-table-style mapping of backend rename validation codes to friendly UI strings. */
const RENAME_ERROR_MAP: Readonly<Record<string, string>> = {
  NAME_TAKEN:          'A template with this name already exists. Choose a different name.',
  NAME_REQUIRED:       'Template name is required.',
  NAME_TOO_LONG:       'Template name must be 100 characters or fewer.',
  NAME_INVALID_FORMAT: 'Template name must start with an alphanumeric character and contain only letters, digits, spaces, hyphens, or underscores.',
};

/** UserPrefs page key for the VM connection template info banner. */
const BANNER_PAGE = 'vm-connection-templates';

/**
 * Standalone management page for the VM Connection Template aggregate. Mirrors the
 * Policy Template list page byte-for-byte: searchable + sortable mat-table, RDP/VNC
 * filter chips, info banner with persistent dismissal, and a right-side details drawer
 * with rename + delete actions. The "Add" button opens the shared in-place vertical
 * create panel — there is no separate `/create` route by design (per the round-3
 * feedback to keep create flow inline with attach surfaces).
 */
@SidebarNavMenuItem({
  route: '/vm-connection-template',
  faIcon: 'settings_input_hdmi',
  name: 'VM Connection Templates',
  // 1290 places the entry directly above Policy Templates (1300) per the agreed sidebar order.
  order: 1290,
  businessProcessesName: 'VM Connection Templates',
})
@Component({
  selector: 'app-vm-connection-template-list',
  templateUrl: './vm-connection-template-list.component.html',
  styleUrls: ['./vm-connection-template-list.component.css'],
})
export class VmConnectionTemplateListComponent implements OnInit, AfterViewInit {
  private readonly logger = Log.create(VmConnectionTemplateListComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  allTemplates: VmConnectionTemplateSummary[] = [];
  dataSource = new MatTableDataSource<VmConnectionTemplateSummary>([]);
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;
  /** `true` when the current user is a management-team admin and may archive entities. */
  get canArchive(): boolean { return this.authService.canArchive(); }
  /** Active table columns; 'actions' is included only for management-team admins. */
  get displayedColumns(): string[] {
    const base = ['protocol', 'templateName', 'description', 'updatedAt'];
    return this.canArchive ? [...base, 'actions'] : base;
  }

  searchTerm = '';
  /** Independent toggle chips — both off or both on = show all. */
  rdpChipActive = false;
  vncChipActive = false;

  loading = false;
  error: string | null = null;

  // ── Detail drawer ──────────────────────────────────────────────────────────
  showDetailDrawer = false;
  selectedDetail: VmConnectionTemplateDetail | null = null;
  loadingDetail = false;

  // ── Rename in-drawer state ─────────────────────────────────────────────────
  isRenaming = false;
  renameNameDraft = '';
  renameDescDraft = '';
  renameError: string | null = null;

  // ── Banner state ───────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  // ── Inline create panel ────────────────────────────────────────────────────
  showCreatePanel = false;

  constructor(
    private readonly templateService: VmConnectionTemplateService,
    private readonly userPrefs: UserPrefsService,
    private readonly authService: AuthService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly notification: NotificationService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadTemplates();

    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(BANNER_PAGE);

    this.dataSource.filterPredicate = (row, filter: string) =>
      (row.templateName?.toLowerCase().includes(filter) ?? false)
      || (row.description?.toLowerCase().includes(filter) ?? false);

    this.dataSource.sortingDataAccessor = (row, sortHeaderId: string) => {
      if (sortHeaderId === 'templateName') return row.templateName?.toLowerCase() ?? '';
      if (sortHeaderId === 'description')  return row.description?.toLowerCase() ?? '';
      if (sortHeaderId === 'protocol')     return row.protocol ?? '';
      if (sortHeaderId === 'updatedAt')    return row.updatedAt ?? '';
      return '';
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /** Hides the info banner; persists "don't show again" when checked. */
  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(BANNER_PAGE);
    }
  }

  loadTemplates(): void {
    this.loading = true;
    this.error = null;
    this.templateService.getAll().subscribe({
      next: list => {
        this.allTemplates = list ?? [];
        this.applyFilters();
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load VM connection templates. Please try again.';
        this.loading = false;
        this.logger.e('Error loading VM connection templates', err);
      },
    });
  }

  toggleRdpChip(): void {
    this.rdpChipActive = !this.rdpChipActive;
    this.applyFilters();
  }

  toggleVncChip(): void {
    this.vncChipActive = !this.vncChipActive;
    this.applyFilters();
  }

  private applyFilters(): void {
    const bothOff = !this.rdpChipActive && !this.vncChipActive;
    const bothOn  =  this.rdpChipActive &&  this.vncChipActive;

    this.dataSource.data = (bothOff || bothOn)
      ? this.allTemplates
      : this.allTemplates.filter(t =>
          (this.rdpChipActive && t.protocol === 'Rdp') ||
          (this.vncChipActive && t.protocol === 'Vnc'));

    this.applySearchFilter();
  }

  applySearchFilter(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onSearchChange(): void {
    this.applySearchFilter();
  }

  onRowClick(row: VmConnectionTemplateSummary): void {
    this.viewDetail(row);
  }

  viewDetail(row: VmConnectionTemplateSummary): void {
    this.loadingDetail = true;
    this.selectedDetail = null;
    this.showDetailDrawer = true;
    this.cancelRename();
    this.templateService.getById(row.id).subscribe({
      next: detail => {
        this.selectedDetail = detail;
        this.loadingDetail = false;
      },
      error: err => {
        this.logger.e('Error loading template detail', err);
        this.error = 'Failed to load template details.';
        this.loadingDetail = false;
        this.showDetailDrawer = false;
      },
    });
  }

  closeDetailDrawer(): void {
    this.showDetailDrawer = false;
    this.selectedDetail = null;
    this.loadingDetail = false;
    this.cancelRename();
  }

  // ── Rename ────────────────────────────────────────────────────────────────
  beginRename(): void {
    if (!this.selectedDetail) return;
    this.isRenaming = true;
    this.renameNameDraft = this.selectedDetail.templateName;
    this.renameDescDraft = this.selectedDetail.description ?? '';
    this.renameError = null;
  }

  cancelRename(): void {
    this.isRenaming = false;
    this.renameNameDraft = '';
    this.renameDescDraft = '';
    this.renameError = null;
  }

  saveRename(): void {
    if (!this.selectedDetail || !this.renameNameDraft.trim()) {
      this.renameError = 'Template name is required.';
      return;
    }

    this.templateService.rename(this.selectedDetail.id, {
      templateName: this.renameNameDraft.trim(),
      description:  this.renameDescDraft.trim(),
    }).subscribe({
      next: updated => {
        this.selectedDetail = updated;
        this.isRenaming = false;
        this.notification.notify('Template renamed.', 'success', 2500);
        this.refreshSummaryRow(updated);
      },
      error: err => {
        const code: string | undefined = err?.error?.error;
        this.renameError = RENAME_ERROR_MAP[code ?? '']
          ?? err?.error?.message ?? 'Failed to rename template.';
      },
    });
  }

  /** Updates the summary row in `allTemplates` after a successful rename. */
  private refreshSummaryRow(updated: VmConnectionTemplateDetail): void {
    const idx = this.allTemplates.findIndex(t => t.id === updated.id);
    if (idx === -1) return;
    this.allTemplates[idx] = {
      id:           updated.id,
      templateName: updated.templateName,
      description:  updated.description,
      protocol:     updated.protocol,
      createdAt:    updated.createdAt,
      updatedAt:    updated.updatedAt,
    };
    this.applyFilters();
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  deleteTemplate(): void {
    if (!this.selectedDetail) return;
    const id = this.selectedDetail.id;
    const name = this.selectedDetail.templateName;

    this.confirmDialog.confirm({
      title: 'Delete VM connection template?',
      message:
        `"${name}" will be permanently deleted. Classes that currently reference it will fall ` +
        'back to the global server defaults at next launch.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'warning',
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.templateService.delete(id).subscribe({
        next: () => {
          this.notification.notify('Template deleted.', 'success', 2500);
          this.allTemplates = this.allTemplates.filter(t => t.id !== id);
          this.applyFilters();
          this.closeDetailDrawer();
        },
        error: err => {
          this.logger.e('Error deleting template', err);
          this.notification.notify('Failed to delete template.', 'error', 3000);
        },
      });
    });
  }

  // ── Inline create panel ───────────────────────────────────────────────────
  openCreatePanel(): void {
    this.showCreatePanel = true;
  }

  closeCreatePanel(): void {
    this.showCreatePanel = false;
  }

  onTemplateCreated(detail: VmConnectionTemplateDetail): void {
    this.showCreatePanel = false;
    this.notification.notify(`Template "${detail.templateName}" created.`, 'success', 2500);
    this.loadTemplates();
  }

  /** Picks an existing duplicate from the create panel — opens its details drawer. */
  onPickExistingDuplicate(existing: VmConnectionTemplateSummary): void {
    this.showCreatePanel = false;
    this.viewDetail(existing);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  protocolLabel(p: VmConnectionProtocol): string {
    return p === 'Rdp' ? 'RDP' : 'VNC';
  }

  get hasActiveFilter(): boolean {
    return this.rdpChipActive || this.vncChipActive;
  }

  // ── Archive ───────────────────────────────────────────────────────────────

  /**
   * Prompts the user then archives the VM connection template.
   * No hard dependency block — simply archives and notifies.
   */
  archiveTemplate(row: VmConnectionTemplateSummary): void {
    this.confirmDialog.confirmAction(
      'Archive VM Connection Template',
      `Archive "${row.templateName}"? Archived templates are hidden from the list.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.templateService.archive(row.id).subscribe({
        next: () => {
          this.notification.notify(`Template "${row.templateName}" archived.`, 'success', 3000);
          this.loadTemplates();
        },
        error: () => {
          this.snackBar.open('Failed to archive template.', 'Dismiss', { duration: 4000 });
        }
      });
    });
  }
}
